import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Minus, ChevronDown, ChevronRight, Download, Save, Trash2, DoorOpen, Square, Maximize, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { getCurrentUserId } from '../../utils/currentUser';

const API = process.env.REACT_APP_BACKEND_URL;

const OPENING_TYPES = [
  { id: 'ext_door', label: 'Exterior Door', category: 'doors', icon: '🚪', defaultWidth: '36"', defaultHeight: '80"' },
  { id: 'int_door', label: 'Interior Door', category: 'doors', icon: '🚪', defaultWidth: '32"', defaultHeight: '80"' },
  { id: 'sliding_door', label: 'Sliding/Patio Door', category: 'doors', icon: '🚪', defaultWidth: '72"', defaultHeight: '80"' },
  { id: 'french_door', label: 'French Door', category: 'doors', icon: '🚪', defaultWidth: '60"', defaultHeight: '80"' },
  { id: 'garage_door', label: 'Garage Door', category: 'doors', icon: '🚪', defaultWidth: '16\'', defaultHeight: '7\'' },
  { id: 'window_dh', label: 'Double Hung Window', category: 'windows', icon: '🪟', defaultWidth: '36"', defaultHeight: '60"' },
  { id: 'window_case', label: 'Casement Window', category: 'windows', icon: '🪟', defaultWidth: '30"', defaultHeight: '48"' },
  { id: 'window_fixed', label: 'Fixed/Picture Window', category: 'windows', icon: '🪟', defaultWidth: '48"', defaultHeight: '48"' },
  { id: 'window_slider', label: 'Sliding Window', category: 'windows', icon: '🪟', defaultWidth: '48"', defaultHeight: '36"' },
  { id: 'skylight', label: 'Skylight', category: 'windows', icon: '🪟', defaultWidth: '24"', defaultHeight: '48"' },
  { id: 'pass_through', label: 'Pass-Through/Cased Opening', category: 'specialty', icon: '⬜', defaultWidth: '48"', defaultHeight: '80"' },
  { id: 'arch', label: 'Arched Opening', category: 'specialty', icon: '⬜', defaultWidth: '36"', defaultHeight: '84"' },
  { id: 'transom', label: 'Transom', category: 'specialty', icon: '⬜', defaultWidth: '36"', defaultHeight: '14"' },
  { id: 'sidelight', label: 'Sidelight', category: 'specialty', icon: '⬜', defaultWidth: '14"', defaultHeight: '80"' },
];

export default function OpeningCountTool({ darkMode = false, projectId = null, onBack }) {
  const [rooms, setRooms] = useState([]);
  const [openings, setOpenings] = useState({});
  const [expandedRoom, setExpandedRoom] = useState(null);
  const [addingTo, setAddingTo] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [loading, setLoading] = useState(false);
  const saveTimerRef = useRef(null);

  const bg = darkMode ? 'bg-[#0D0D0D]' : 'bg-[#F8F7F5]';
  const cardBg = darkMode ? 'bg-[#1A1A1E]' : 'bg-white';
  const border = darkMode ? 'border-[#2A2A2A]' : 'border-gray-200';
  const text = darkMode ? 'text-gray-100' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';

  // Load rooms and saved openings
  useEffect(() => {
    if (!projectId) return;
    const load = async () => {
      setLoading(true);
      try {
        const roomRes = await axios.get(`${API}/api/projects/${projectId}/rooms?user_id=${getCurrentUserId()}`);
        const roomData = roomRes.data?.rooms || (Array.isArray(roomRes.data) ? roomRes.data : []);
        setRooms(roomData);
        if (roomData.length > 0) setExpandedRoom(roomData[0].id || roomData[0].room_name);
      } catch {}
      // Load saved openings
      try {
        const res = await axios.get(`${API}/api/design-companion/openings/${projectId}`);
        if (res.data?.openings) setOpenings(res.data.openings);
        if (res.data?.updated_at) setLastSaved(res.data.updated_at);
      } catch {}
      setLoading(false);
    };
    load();
  }, [projectId]);

  // Auto-save debounced
  useEffect(() => {
    if (!projectId || Object.keys(openings).length === 0) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.put(`${API}/api/design-companion/openings/${projectId}`, { openings });
        if (res.data?.updated_at) setLastSaved(res.data.updated_at);
      } catch {}
    }, 1500);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [openings, projectId]);

  const addOpening = (roomKey, typeId) => {
    const typeDef = OPENING_TYPES.find(t => t.id === typeId);
    if (!typeDef) return;
    const newOpening = {
      id: crypto.randomUUID(),
      type_id: typeId,
      label: typeDef.label,
      category: typeDef.category,
      width: typeDef.defaultWidth,
      height: typeDef.defaultHeight,
      qty: 1,
      notes: '',
    };
    setOpenings(prev => ({
      ...prev,
      [roomKey]: [...(prev[roomKey] || []), newOpening],
    }));
    setAddingTo(null);
  };

  const updateOpening = (roomKey, openingId, field, value) => {
    setOpenings(prev => ({
      ...prev,
      [roomKey]: (prev[roomKey] || []).map(o => o.id === openingId ? { ...o, [field]: value } : o),
    }));
  };

  const removeOpening = (roomKey, openingId) => {
    setOpenings(prev => ({
      ...prev,
      [roomKey]: (prev[roomKey] || []).filter(o => o.id !== openingId),
    }));
  };

  const adjustQty = (roomKey, openingId, delta) => {
    setOpenings(prev => ({
      ...prev,
      [roomKey]: (prev[roomKey] || []).map(o =>
        o.id === openingId ? { ...o, qty: Math.max(1, (o.qty || 1) + delta) } : o
      ),
    }));
  };

  // Totals
  const getTotals = useCallback(() => {
    const totals = { doors: 0, windows: 0, specialty: 0, total: 0 };
    Object.values(openings).forEach(roomOpenings => {
      roomOpenings.forEach(o => {
        const qty = o.qty || 1;
        totals[o.category] = (totals[o.category] || 0) + qty;
        totals.total += qty;
      });
    });
    return totals;
  }, [openings]);

  // Export CSV
  const exportCSV = () => {
    const rows = [['Room', 'Type', 'Category', 'Width', 'Height', 'Qty', 'Notes']];
    Object.entries(openings).forEach(([roomKey, roomOpenings]) => {
      const room = rooms.find(r => (r.id || r.room_name) === roomKey);
      const roomName = room?.name || room?.room_name || roomKey;
      roomOpenings.forEach(o => {
        rows.push([roomName, o.label, o.category, o.width, o.height, String(o.qty || 1), o.notes || '']);
      });
    });
    const totals = getTotals();
    rows.push([]);
    rows.push(['TOTALS', '', '', '', '', '', '']);
    rows.push(['Doors', '', '', '', '', String(totals.doors), '']);
    rows.push(['Windows', '', '', '', '', String(totals.windows), '']);
    rows.push(['Specialty', '', '', '', '', String(totals.specialty), '']);
    rows.push(['Total Openings', '', '', '', '', String(totals.total), '']);

    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `opening-count-${projectId || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported opening count CSV');
  };

  const totals = getTotals();

  return (
    <div className={`p-6 space-y-5 ${bg}`} data-testid="opening-count-tool">
      {/* Header */}
      <div>
        {onBack && (
          <button onClick={onBack} className={`flex items-center gap-1 text-xs ${textMuted} hover:text-[#BC5D41] mb-2`}>
            <ArrowLeft className="w-3 h-3" /> Back to Live Capture
          </button>
        )}
        <div className="flex items-center justify-between">
          <div>
            <h2 className={`text-lg font-semibold ${text}`}>Opening Count</h2>
            <p className={`text-sm ${textMuted}`}>Track doors, windows, and openings by room</p>
          </div>
          <div className="flex items-center gap-2">
            {lastSaved && (
              <span className={`text-[10px] ${textMuted} flex items-center gap-1`} data-testid="openings-save-status">
                <Save className="w-3 h-3" /> Saved {new Date(lastSaved).toLocaleTimeString()}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={exportCSV} className="text-[10px] h-7 px-2" data-testid="openings-export-csv">
              <Download className="w-3 h-3 mr-1" /> CSV
            </Button>
          </div>
        </div>
        {!projectId && (
          <div className={`mt-2 text-[11px] ${textMuted} italic`}>Select a project to track openings per room</div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Doors', count: totals.doors, color: '#BC5D41' },
          { label: 'Windows', count: totals.windows, color: '#3B82F6' },
          { label: 'Specialty', count: totals.specialty, color: '#8B5CF6' },
          { label: 'Total', count: totals.total, color: '#10B981' },
        ].map(s => (
          <div key={s.label} className={`${cardBg} border ${border} rounded-xl p-4 text-center`} data-testid={`opening-total-${s.label.toLowerCase()}`}>
            <div className="text-2xl font-bold" style={{ color: s.color }}>{s.count}</div>
            <div className={`text-[10px] ${textMuted} mt-1`}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Room-by-Room Accordion */}
      {loading ? (
        <div className={`text-center py-8 ${textMuted} text-sm`}>Loading rooms...</div>
      ) : rooms.length === 0 ? (
        <div className={`${cardBg} border ${border} rounded-xl p-8 text-center`}>
          <p className={`text-sm ${textMuted}`}>No rooms found. Add rooms in Room Takeoff first.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rooms.map(room => {
            const roomKey = room.id || room.room_name;
            const roomName = room.name || room.room_name || roomKey;
            const roomOpenings = openings[roomKey] || [];
            const isExpanded = expandedRoom === roomKey;
            const roomDoors = roomOpenings.filter(o => o.category === 'doors').reduce((s, o) => s + (o.qty || 1), 0);
            const roomWindows = roomOpenings.filter(o => o.category === 'windows').reduce((s, o) => s + (o.qty || 1), 0);
            const roomSpec = roomOpenings.filter(o => o.category === 'specialty').reduce((s, o) => s + (o.qty || 1), 0);

            return (
              <div key={roomKey} className={`${cardBg} border ${border} rounded-xl overflow-hidden`} data-testid={`room-openings-${roomKey}`}>
                <button onClick={() => setExpandedRoom(isExpanded ? null : roomKey)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left">
                  {isExpanded ? <ChevronDown className={`w-4 h-4 ${textMuted}`} /> : <ChevronRight className={`w-4 h-4 ${textMuted}`} />}
                  <span className={`flex-1 text-sm font-medium ${text}`}>{roomName}</span>
                  <div className="flex gap-2 text-[10px]">
                    {roomDoors > 0 && <span className="px-2 py-0.5 rounded-full bg-[#BC5D41]/10 text-[#BC5D41] font-medium">{roomDoors}D</span>}
                    {roomWindows > 0 && <span className="px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-500 font-medium">{roomWindows}W</span>}
                    {roomSpec > 0 && <span className="px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-500 font-medium">{roomSpec}S</span>}
                  </div>
                </button>
                {isExpanded && (
                  <div className={`border-t ${border} px-4 py-3 space-y-2`}>
                    {/* Existing openings */}
                    {roomOpenings.map(opening => (
                      <div key={opening.id} className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? 'bg-[#0D0D0D]' : 'bg-gray-50'}`}>
                        <div className="flex-1 min-w-0">
                          <div className={`text-xs font-medium ${text}`}>{opening.label}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <input type="text" value={opening.width} onChange={(e) => updateOpening(roomKey, opening.id, 'width', e.target.value)}
                              className={`w-16 text-[10px] px-1.5 py-0.5 rounded border ${border} ${darkMode ? 'bg-[#111] text-gray-200' : 'bg-white text-gray-800'}`} placeholder="W" />
                            <span className={`text-[10px] ${textMuted}`}>x</span>
                            <input type="text" value={opening.height} onChange={(e) => updateOpening(roomKey, opening.id, 'height', e.target.value)}
                              className={`w-16 text-[10px] px-1.5 py-0.5 rounded border ${border} ${darkMode ? 'bg-[#111] text-gray-200' : 'bg-white text-gray-800'}`} placeholder="H" />
                            <input type="text" value={opening.notes} onChange={(e) => updateOpening(roomKey, opening.id, 'notes', e.target.value)}
                              className={`flex-1 text-[10px] px-1.5 py-0.5 rounded border ${border} ${darkMode ? 'bg-[#111] text-gray-200' : 'bg-white text-gray-800'}`} placeholder="Notes..." />
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => adjustQty(roomKey, opening.id, -1)}
                            className={`w-6 h-6 rounded flex items-center justify-center ${darkMode ? 'bg-[#222] text-gray-400' : 'bg-gray-200 text-gray-600'} hover:bg-[#BC5D41] hover:text-white`}>
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className={`w-6 text-center text-xs font-bold ${text}`}>{opening.qty || 1}</span>
                          <button onClick={() => adjustQty(roomKey, opening.id, 1)}
                            className={`w-6 h-6 rounded flex items-center justify-center ${darkMode ? 'bg-[#222] text-gray-400' : 'bg-gray-200 text-gray-600'} hover:bg-[#BC5D41] hover:text-white`}>
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <button onClick={() => removeOpening(roomKey, opening.id)}
                          className={`p-1 rounded ${textMuted} hover:text-red-500`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}

                    {/* Add opening button/selector */}
                    {addingTo === roomKey ? (
                      <div className={`p-3 rounded-lg border-2 border-dashed ${darkMode ? 'border-[#333]' : 'border-gray-300'}`}>
                        <p className={`text-[10px] font-semibold mb-2 ${textMuted}`}>Select opening type:</p>
                        <div className="space-y-1">
                          {['doors', 'windows', 'specialty'].map(cat => (
                            <div key={cat}>
                              <p className={`text-[9px] font-bold uppercase tracking-wider mb-1 ${textMuted}`}>{cat}</p>
                              <div className="grid grid-cols-2 gap-1 mb-2">
                                {OPENING_TYPES.filter(t => t.category === cat).map(type => (
                                  <button key={type.id} onClick={() => addOpening(roomKey, type.id)}
                                    className={`text-left px-2 py-1.5 rounded text-[10px] ${darkMode ? 'hover:bg-[#222] text-gray-300' : 'hover:bg-gray-100 text-gray-700'} transition-colors`}
                                    data-testid={`add-opening-${type.id}`}>
                                    {type.label}
                                    <span className={`ml-1 text-[9px] ${textMuted}`}>{type.defaultWidth}×{type.defaultHeight}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <button onClick={() => setAddingTo(null)} className={`text-[10px] ${textMuted} hover:text-[#BC5D41] mt-1`}>Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => setAddingTo(roomKey)}
                        className={`w-full flex items-center justify-center gap-1 py-2 rounded-lg border-2 border-dashed ${darkMode ? 'border-[#333] text-gray-500' : 'border-gray-200 text-gray-400'} hover:border-[#BC5D41] hover:text-[#BC5D41] transition-colors text-[11px]`}
                        data-testid={`add-opening-to-${roomKey}`}>
                        <Plus className="w-3.5 h-3.5" /> Add Opening
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
